import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { useProjects } from '@battleops/client/src/context/ProjectContext';
import { useNotes } from '@battleops/client/src/context/NoteContext';
import { useSession } from '@battleops/client/src/context/SessionContext';
import { useAvatarUri } from './Crest';
import { useLocalCollection } from '@battleops/client/src/data/useLocal';
import { formatDurationShort, computeTotalTaskTime, photosOfTask, PHOTO_TAG_LABEL, localToday, localDateStr, noteLinks } from '@battleops/shared';
import Icon from './Icon';
import Crest from './Crest';
import TaskList from './TaskList';
import { useStartTask, hhmm } from '../lib/actions';
import { useNow } from '../hooks';
import s from './TaskModal.module.css';

const dayWord = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }) : '—');
const at = (ms) => (ms ? new Date(ms).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

// Everything about one task, in one place — reusable anywhere (the planner opens it; so can any list): its photos, what
// it is, its list, its subtasks and notes, and a small dashboard — time against the estimate, who worked on it and when, the
// trips on the days it was worked, its dates. Title, description, date, estimate and subtasks edit in place.
export default function TaskModal({ id, onClose }) {
  const { tasks, getChildren, updateTask, setTaskDone, resetTask, addTask, activeTaskId, timer, clockSessions, allClockSessions } = useTasks();
  const { projects } = useProjects();
  const { allNotes } = useNotes();
  const members = useLocalCollection('members') || [];
  const mediaDocs = useLocalCollection('media');   // the shared photo index (v0.1.266)
  const { identity } = useSession();
  const myAvatar = useAvatarUri(identity?.avatarPath);
  const navigate = useNavigate();
  const start = useStartTask();
  const task = tasks.find((t) => t.id === id);
  const now = useNow(activeTaskId === id ? 1000 : 60000);
  const [title, setTitle] = useState(task?.title || '');
  const [desc, setDesc] = useState(task?.description || '');
  const [lightbox, setLightbox] = useState(null);
  useEffect(() => { setTitle(task?.title || ''); setDesc(task?.description || ''); }, [task?.id, task?.title, task?.description]);
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') { if (lightbox) setLightbox(null); else onClose(); } };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose, lightbox]);

  const sessions = allClockSessions || clockSessions || [];
  // Who worked on it and when: the clock sessions' task segments (per person), and the time entries (per day).
  const work = useMemo(() => {
    const byPerson = new Map(); const days = new Set(); let first = null; let last = null; let n = 0;
    for (const ses of sessions) {
      for (const seg of ses.taskSegments || []) {
        if (seg.taskId !== id || !seg.startedAt) continue;
        const ms = Math.max(0, (seg.endedAt || Date.now()) - seg.startedAt);
        const who = ses.ownerUid || 'me';
        byPerson.set(who, (byPerson.get(who) || 0) + ms);
        days.add(localDateStr(seg.startedAt)); n++;
        first = first == null ? seg.startedAt : Math.min(first, seg.startedAt);
        last = last == null ? seg.endedAt || Date.now() : Math.max(last, seg.endedAt || Date.now());
      }
    }
    for (const e of task?.timeEntries || []) if (e.date) days.add(e.date);
    return { byPerson: [...byPerson.entries()].sort((a, b) => b[1] - a[1]), days: [...days].sort(), first, last, segments: n };
  }, [sessions, id, task?.timeEntries]);
  const byDay = useMemo(() => {
    const m = new Map();
    for (const e of task?.timeEntries || []) { if (!m.has(e.date)) m.set(e.date, []); m.get(e.date).push(e); }
    return [...m.entries()].sort((a, b) => String(b[0]).localeCompare(String(a[0]))).map(([date, list]) => ({ date, list, ms: list.reduce((a, e) => a + (e.duration || 0), 0) }));
  }, [task?.timeEntries]);
  // Trips on the days it was worked (the drives the phone recorded, same job or no job).
  const trips = useMemo(() => {
    const days = new Set(work.days);
    return sessions.filter((x) => x.type === 'travel' && days.has(x.date || localDateStr(x.startedAt)) && (!x.projectId || !task?.projectId || x.projectId === task.projectId))
      .sort((a, b) => b.startedAt - a.startedAt).slice(0, 8);
  }, [sessions, work.days, task?.projectId]);
  const notes = useMemo(() => allNotes.filter((n) => !n.deletedAt && noteLinks(n).some((l) => l.type === 'task' && l.id === id)), [allNotes, id]);

  if (!task) return <Shell onClose={onClose}><div className={s.gone}>This task is gone — deleted, or not synced to this browser yet.</div></Shell>;
  const project = projects.find((p) => p.id === task.projectId);
  const color = project?.color || 'var(--c-accent)';
  const running = activeTaskId === task.id;
  const spent = running && timer?.taskId === task.id ? computeTotalTaskTime(task, timer, now) : task.timeSpent || 0;
  const est = (task.estimatedMinutes || 0) * 60000;
  const pct = est ? Math.min(1.5, spent / est) : null;
  const done = task.status === 'done';
  const photos = photosOfTask(task, mediaDocs);
  const subs = getChildren(task.id);
  const who = (uid) => (uid === 'me' || uid === identity?.uid ? { name: identity?.name || 'You', me: true } : members.find((m) => m.uid === uid || m.id === uid));
  const saveTitle = () => { const t = title.trim(); if (t && t !== task.title) updateTask({ id: task.id, title: t }); else setTitle(task.title); };
  const saveDesc = () => { if ((desc || '') !== (task.description || '')) updateTask({ id: task.id, description: desc || null }); };
  const timeline = [...(task.noteEvents || []).map((n) => ({ at: n.at || 0, text: n.text })), ...(task.statusLog || []).map((x) => ({ at: x.at || 0, text: x.status ? `Marked ${x.status}` : x.text || '' }))]
    .filter((x) => x.text).sort((a, b) => b.at - a.at).slice(0, 12);

  return (
    <Shell onClose={onClose} color={color}>
      <header className={s.head}>
        {project ? <button className={s.proj} onClick={() => { onClose(); navigate(`/projects/${project.id}`); }}><span className="dot" style={{ background: project.color }} />{project.icon} {project.name}</button> : <span className={s.proj}>No job</span>}
        <span className={s.status} data-s={done ? 'done' : running ? 'running' : 'todo'}>{done ? 'Done' : running ? 'Running' : 'Open'}</span>
        {task.priority && task.priority !== 'medium' ? <span className={s.prio} data-p={task.priority}>{task.priority}</span> : null}
        <span className="spacer" />
        <button className={s.x} onClick={onClose} aria-label="Close"><Icon name="x" size={18} /></button>
      </header>

      <div className={s.scroll}>
        <input className={s.title} value={title} onChange={(e) => setTitle(e.target.value)} onBlur={saveTitle} onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} aria-label="Title" data-done={done || undefined} />
        <div className={s.actions}>
          {!done ? (running ? <span className={s.live}><span className={s.pulse} />{formatDurationShort(spent)} running</span>
            : <button className={s.startBtn} onClick={() => start(task)}><Icon name="play" size={15} />Start</button>) : null}
          <button className="btn" onClick={() => (done ? resetTask(task.id) : setTaskDone(task.id, true))}><Icon name={done ? 'rotate-ccw' : 'check'} size={15} />{done ? 'Reopen' : 'Done'}</button>
          <label className={s.inline}><Icon name="calendar" size={14} /><input type="date" value={task.scheduledDate || ''} onChange={(e) => updateTask({ id: task.id, scheduledDate: e.target.value || null })} aria-label="Day" /></label>
          <label className={s.inline}><Icon name="hourglass" size={14} />
            <select value={task.estimatedMinutes || ''} onChange={(e) => updateTask({ id: task.id, estimatedMinutes: Number(e.target.value) || null })} aria-label="Estimate">
              <option value="">No estimate</option>{[15, 30, 45, 60, 90, 120, 180, 240, 360, 480, 960].map((m) => <option key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60} h`}</option>)}
            </select>
          </label>
          {task.unit || task.qty ? (
            <label className={s.inline} title="The quoted quantity (from the bid or the template)"><Icon name="hash" size={14} />
              <input type="number" min="0" step="any" defaultValue={task.qty ?? ''} key={`q-${task.qty}`} style={{ width: 80 }} aria-label="Quantity"
                onBlur={(e) => { const v = e.target.value === '' ? null : Number(e.target.value); if (v !== (task.qty ?? null)) updateTask({ id: task.id, qty: v }); }} />
              <span className="faint">{task.unit || ''}</span>
            </label>
          ) : null}
        </div>

        <div className={s.cols}>
          <div className={s.main}>
            {photos.length ? (
              <section className={s.sec}>
                <h3>Photos <span>{photos.length}</span></h3>
                <div className={s.photos}>
                  {photos.map((p) => (
                    <button key={p.uri} className={s.photo} onClick={() => setLightbox(p)} title={[p.at ? new Date(p.at).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null, p.caption].filter(Boolean).join(' · ')}>
                      <img src={p.uri} alt={p.caption || PHOTO_TAG_LABEL[p.tag]} loading="lazy" /><span className={s.tag} data-t={p.tag}>{PHOTO_TAG_LABEL[p.tag]}</span>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}
            <section className={s.sec}>
              <h3>What it is</h3>
              <textarea className={s.desc} value={desc} onChange={(e) => setDesc(e.target.value)} onBlur={saveDesc} placeholder="Add the details — what, where, materials, gotchas…" rows={4} />
            </section>
            <section className={s.sec}>
              <h3>List</h3>
              <TaskList task={task} departmentId={task.departmentId || project?.departmentId || null} />
            </section>
            <section className={s.sec}>
              <h3>Subtasks <span>{subs.filter((x) => x.status === 'done').length}/{subs.length}</span></h3>
              {subs.map((x) => (
                <label key={x.id} className={s.sub} data-done={x.status === 'done' || undefined}>
                  <input type="checkbox" checked={x.status === 'done'} onChange={() => setTaskDone(x.id, x.status !== 'done')} /><span>{x.title}</span>
                </label>
              ))}
              <div className={s.addSub}><Icon name="plus" size={14} /><input placeholder="Add a subtask…" onKeyDown={(e) => { const v = e.currentTarget.value.trim(); if (e.key === 'Enter' && v) { addTask({ title: v, projectId: task.projectId, parentId: task.id, scheduledDate: null, priority: 'medium' }); e.currentTarget.value = ''; } }} /></div>
            </section>
            {notes.length ? (
              <section className={s.sec}>
                <h3>Notes <span>{notes.length}</span></h3>
                {notes.map((n) => <button key={n.id} className={s.note} onClick={() => { onClose(); navigate(`/notes/all/${n.id}`); }}><Icon name="file-text" size={14} /><span>{n.title || 'Untitled'}</span><span className="faint num">{n.date?.slice(5)}</span></button>)}
              </section>
            ) : null}
            {timeline.length ? (
              <section className={s.sec}>
                <h3>Activity</h3>
                {timeline.map((x, k) => <div key={k} className={s.event}><span className={s.dotLine} /><div><div>{x.text}</div><div className="faint num" style={{ fontSize: 11.5 }}>{at(x.at)}</div></div></div>)}
              </section>
            ) : null}
          </div>

          <aside className={s.dash}>
            <div className={s.card}>
              <div className={s.cardLabel}>Time</div>
              <div className={s.big}><span className="num">{formatDurationShort(spent) || '0m'}</span>{est ? <span className={s.of}>of {formatDurationShort(est)}</span> : null}</div>
              {pct != null ? <div className={s.bar}><span style={{ width: `${Math.min(100, pct * 100)}%`, background: pct > 1 ? 'var(--c-warning)' : 'var(--c-success)' }} /></div> : <div className={s.hint}>Set an estimate to track it against time.</div>}
              <div className={s.stats}>
                <div><b className="num">{work.days.length}</b><span>day{work.days.length === 1 ? '' : 's'} worked</span></div>
                <div><b className="num">{work.segments || byDay.reduce((a, d) => a + d.list.length, 0)}</b><span>sessions</span></div>
              </div>
            </div>
            <div className={s.card}>
              <div className={s.cardLabel}>Dates</div>
              <dl className={s.dl}>
                <dt>Planned</dt><dd>{dayWord(task.scheduledDate)}{Number.isFinite(task.plannedStart) ? ` · ${new Date(2000, 0, 1, Math.floor(task.plannedStart / 60), task.plannedStart % 60).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}` : ''}</dd>
                {task.remindTime ? <><dt>Reminder</dt><dd>{at(task.remindTime)}</dd></> : null}
                <dt>First worked</dt><dd>{at(work.first)}</dd>
                <dt>Last worked</dt><dd>{at(work.last)}</dd>
                <dt>Created</dt><dd>{at(task.createdAt)}</dd>
                {done ? <><dt>Done</dt><dd>{at(task.completedAt)}</dd></> : null}
              </dl>
            </div>
            <div className={s.card}>
              <div className={s.cardLabel}>Who worked on it</div>
              {work.byPerson.map(([uid, ms]) => {
                const m = who(uid);
                return <div key={uid} className={s.person}><Crest width={22} uri={m?.me ? myAvatar : null} initials={(m?.name || '?').charAt(0).toUpperCase()} border="var(--c-accent)" /><span>{m?.name || 'A teammate'}</span><b className="num">{formatDurationShort(ms)}</b></div>;
              })}
              {!work.byPerson.length ? <div className={s.hint}>No clocked time on it yet.</div> : null}
              {byDay.length ? <div className={s.days}>{byDay.slice(0, 6).map((d) => <div key={d.date} className={s.dayRow}><span>{d.date === localToday() ? 'Today' : dayWord(d.date)}</span><span className="faint num">{d.list.map((e) => `${e.startTime}–${e.endTime}`).join(', ')}</span><b className="num">{formatDurationShort(d.ms)}</b></div>)}</div> : null}
            </div>
            {trips.length ? (
              <div className={s.card}>
                <div className={s.cardLabel}>Trips on those days</div>
                {trips.map((x) => (
                  <button key={x.id} className={s.trip} onClick={() => { onClose(); navigate(`/trips/${x.date || localDateStr(x.startedAt)}`); }}>
                    <Icon name="navigation" size={13} /><span>{dayWord(x.date || localDateStr(x.startedAt))} · {hhmm(x.startedAt)}</span>
                    <b className="num">{x.distanceM ? `${(x.distanceM / 1000).toFixed(1)} km` : formatDurationShort((x.endedAt || x.startedAt) - x.startedAt)}</b>
                  </button>
                ))}
              </div>
            ) : null}
          </aside>
        </div>
      </div>
      {lightbox ? <div className={s.lightbox} onClick={() => setLightbox(null)}><img src={lightbox.uri} alt={lightbox.caption || ''} /><span className={s.tag} data-t={lightbox.tag}>{PHOTO_TAG_LABEL[lightbox.tag]}</span>
        {lightbox.caption || lightbox.at ? <div style={{ position: 'absolute', left: 0, right: 0, bottom: 24, textAlign: 'center', color: '#fff', fontSize: 15, textShadow: '0 1px 3px #000', padding: '0 24px' }}>
          {lightbox.at ? <div style={{ fontSize: 12, opacity: 0.75 }}>Taken {new Date(lightbox.at).toLocaleString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div> : null}
          {lightbox.caption}
        </div> : null}</div> : null}
    </Shell>
  );
}

function Shell({ children, onClose, color = 'var(--c-accent)' }) {
  return (
    <div className={s.scrim} onMouseDown={onClose}>
      <div className={s.modal} style={{ '--tc': color }} role="dialog" aria-label="Task" onMouseDown={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}
